/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useRef, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'

// Components
import {
    Breadcrumb as ChakraBreadcrumb,
    BreadcrumbItem as ChakraBreadcrumbItem,
    BreadcrumbLink as ChakraBreadcrumbLink,
    // Hooks
    useStyleConfig,
    Box,
    Text,
} from '@chakra-ui/react'

// Icons
import {BreadcrumbSeperatorIcon} from '../icons/shared'

// Others
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'

/**
 * A simplification of the Chakra `Breadcrumb` component for our project needs. Given
 * a list of categories, display a breadcrumb and it's items.
 */
const Breadcrumb = ({categories, homepage = false, ...rest}) => {
    const styles = useStyleConfig('Breadcrumb')
    const {buildUrl} = useMultiSite()
    const {buildLocalizedUrl} = useMulticountryUtils()

    const containerRef = useRef(null)
    const [fadeRight, setFadeRight] = useState(false)
    const [fadeLeft, setFadeLeft] = useState(false)

    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        const check = () => {
            const overflow = el.scrollWidth > el.clientWidth + 1
            const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1
            setFadeRight(overflow && !atEnd)
            setFadeLeft(el.scrollLeft > 1)
        }
        check()
        el.addEventListener('scroll', check, {passive: true})
        window.addEventListener('resize', check)
        return () => {
            el.removeEventListener('scroll', check)
            window.removeEventListener('resize', check)
        }
    }, [])

    return (
        <Box sx={styles.wrapper}>
            <ChakraBreadcrumb ref={containerRef} sx={styles.container} {...rest}>
                {categories?.map((category, index) => {
                    const lastLink = index === categories.length - 1
                    const linkStyle = lastLink ? styles.lastLink : styles.link
                    const categoryUrl = category?.slugUrl || category?.c_slugUrl
                    const hasCategoryUrl = Boolean(categoryUrl)
                    const url = homepage
                        ? buildLocalizedUrl('/', buildUrl)
                        : hasCategoryUrl
                        ? buildLocalizedUrl(categoryUrl)
                        : undefined
                    return (
                        <Fragment key={category.id || `${category.name}-${index}`}>
                            <ChakraBreadcrumbItem
                                data-testid="sf-crumb-item"
                                {...styles.item}
                                sx={{flexShrink: 0}}
                            >
                                {lastLink ? (
                                    <Text
                                        aria-current="page"
                                        tabIndex={0}
                                        sx={{...linkStyle, flexShrink: 0}}
                                    >
                                        {category.name}
                                    </Text>
                                ) : !url ? (
                                    <Text aria-current="page" sx={{...linkStyle, flexShrink: 0}}>
                                        {category.name}
                                    </Text>
                                ) : (
                                    <ChakraBreadcrumbLink
                                        as={RouteLink}
                                        to={url}
                                        sx={{...linkStyle, flexShrink: 0}}
                                    >
                                        {category.name}
                                    </ChakraBreadcrumbLink>
                                )}
                            </ChakraBreadcrumbItem>

                            {!lastLink && <BreadcrumbSeperatorIcon {...styles.icon} />}
                        </Fragment>
                    )
                })}
            </ChakraBreadcrumb>

            {fadeLeft && <Box sx={styles.fadeOverlayLeft} />}
            {fadeRight && <Box sx={styles.fadeOverlayRight} />}
        </Box>
    )
}

Breadcrumb.displayName = 'Breadcrumb'

Breadcrumb.propTypes = {
    /**
     * The categories to be displayed in this breadcrumb.
     */
    categories: PropTypes.array,
    homepage: PropTypes.bool,
}

export default Breadcrumb
